import { useState, useEffect, useRef } from "@wordpress/element";
import { __ } from "@wordpress/i18n";
import {
	Languages,
	ChevronDown,
	Search,
	CheckCheck,
	CircleCheck,
	ArrowLeft,
} from "lucide-react";
import { ScrollArea } from "./ui/scroll-area";
import FlagIcon from "./FlagIcon";
import { Input } from "./ui/input";
import { cn } from "@/lib/utils";
import { Badge } from "./ui/badge";
const LanguageSwitcher = ({ theme, isOpen, onBack }) => {
	const languages = [
		{ code: "en_US", name: "English" },
		{ code: "zh_CN", name: "简体中文" },
		{ code: "es_ES", name: "Español" },
		{ code: "de_DE", name: "Deutsch" },
		{ code: "fr_FR", name: "Français" },
		{ code: "pt_PT", name: "Português" },
		{ code: "ru_RU", name: "Русский" },
		{ code: "ja_JP", name: "日本語" },
		{ code: "ko_KR", name: "한국어" },
		{ code: "ar_SA", name: "العربية" },
		{ code: "hi_IN", name: "हिन्दी" },
		{ code: "bn_BD", name: "বাংলা" },
		{ code: "ur_PK", name: "اردو" },
		{ code: "tr_TR", name: "Türkçe" },
		{ code: "nl_NL", name: "Nederlands" },
		{ code: "it_IT", name: "Italiano" },
		{ code: "pl_PL", name: "Polski" },
		{ code: "cs_CZ", name: "Čeština" },
		{ code: "hu_HU", name: "Magyar" },
		{ code: "uk_UA", name: "Українська" },
		{ code: "ro_RO", name: "Română" },
		{ code: "el_GR", name: "Ελληνικά" },
		{ code: "he_IL", name: "עברית" },
		{ code: "vi_VN", name: "Tiếng Việt" },
		{ code: "th_TH", name: "ไทย" },
		{ code: "ms_MY", name: "Bahasa Melayu" },
		{ code: "id_ID", name: "Bahasa Indonesia" },
		{ code: "sv_SE", name: "Svenska" },
		{ code: "nb_NO", name: "Norsk Bokmål" },
		{ code: "da_DK", name: "Dansk" },
		{ code: "fi_FI", name: "Suomi" },
		{ code: "fa_IR", name: "فارسی" },
	];

	const getInitialLanguage = () => {
		const savedLanguage = localStorage.getItem("accessibly-wp-language");
		return savedLanguage || "en_US";
	};

	const [currentLanguage, setCurrentLanguage] = useState(getInitialLanguage());

	// Force re-render when language changes
	const [, forceUpdate] = useState({});
	const [searchTerm, setSearchTerm] = useState("");
	const searchInputRef = useRef(null);

	useEffect(() => {
		// console.log(
		// 	"🌐 LanguageSwitcher useEffect - Setting language to:",
		// 	currentLanguage,
		// );

		// Set WordPress locale if setLocale is available
		if (
			window.wp &&
			window.wp.i18n &&
			typeof window.wp.i18n.setLocale === "function"
		) {
			window.wp.i18n.setLocale(currentLanguage);
			// console.log(
			// 	"🌐 LanguageSwitcher - WordPress setLocale called with:",
			// 	currentLanguage,
			// );
		} else {
			// console.log(
			// 	"🌐 LanguageSwitcher - WordPress setLocale not available, using fallback",
			// );
		}

		// Save to localStorage
		localStorage.setItem("accessibly-wp-language", currentLanguage);
		// console.log(
		// 	"🌐 LanguageSwitcher - Saved to localStorage:",
		// 	localStorage.getItem("accessibly-wp-language"),
		// );

		// Force re-render to update translations
		forceUpdate({});

		// Dispatch custom event
		window.dispatchEvent(
			new CustomEvent("languageChanged", {
				detail: { language: currentLanguage },
			}),
		);
		//console.log("🌐 LanguageSwitcher - Event dispatched for:", currentLanguage);
	}, [currentLanguage]);

	const handleSelect = (languageCode) => {
		// console.log(
		// 	"🌐 LanguageSwitcher - Changing from:",
		// 	currentLanguage,
		// 	"to:",
		// 	languageCode,
		// );

		setCurrentLanguage(languageCode);
		setSearchTerm(""); // Reset search when selecting

		// Immediately update localStorage
		localStorage.setItem("accessibly-wp-language", languageCode);
		//console.log("🌐 LanguageSwitcher - Updated localStorage to:", languageCode);

		// Dispatch event immediately to ensure synchronization
		window.dispatchEvent(
			new CustomEvent("languageChanged", {
				detail: { language: languageCode },
			}),
		);
		// console.log(
		// 	"🌐 LanguageSwitcher - Event dispatched immediately for:",
		// 	languageCode,
		// );

		if (onBack) onBack(); // Go back to main view after selection

		// console.log(
		// 	"🌐 LanguageSwitcher - Event will also be dispatched in useEffect",
		// );
	};

	// Filter languages based on search term
	const filteredLanguages = languages.filter(
		(lang) =>
			lang.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
			lang.code.toLowerCase().includes(searchTerm.toLowerCase()),
	);

	// Focus search input when component opens
	useEffect(() => {
		if (isOpen && searchInputRef.current) {
			setTimeout(() => {
				searchInputRef.current.focus();
			}, 100);
		} else if (!isOpen) {
			setSearchTerm(""); // Reset search when component closes
		}
	}, [isOpen]);

	const getSelectedLanguage = () => {
		return (
			languages.find((lang) => lang.code === currentLanguage) || languages[0]
		);
	};

	if (!isOpen) return null;

	return (
		<div className="zn:!text-primary-icon-foreground zn:!p-4">
			{/* Header Section - Similar to PageStructureView */}
			<div className="zn:flex zn:items-center zn:justify-between">
				<button
					onClick={onBack}
					className="zn:flex zn:items-center zn:gap-2 zn:cursor-pointer"
				>
					<ArrowLeft className="zn:size-5" />
					<span>{__("Back", "accessibly-wp")}</span>
				</button>
			</div>

			{/* Title Section */}
			<div className="zn:text-center zn:!mb-6">
				<div className="zn:font-semibold zn:md:!text-xl zn:!text-base zn:md:!mb-2 zn:!mb-0.5">
					{__("Language Selection", "accessibly-wp")}
				</div>
				<div className="zn:font-normal zn:md:!text-sm zn:!text-xs">
					{__("Choose your preferred interface language", "accessibly-wp")}
				</div>
			</div>

			{/* Current Language Display */}
			<div className="zn:!mb-4 zn:!p-3 zn:!bg-gray-50 zn:rounded-lg zn:flex zn:items-center zn:gap-3">
				<Badge variant="outline" className="zn:!flex zn:items-center zn:gap-2">
					<Languages className="zn:size-4" />
					{__("Current", "accessibly-wp")}
				</Badge>
				<FlagIcon
					countryCode={getSelectedLanguage().code}
					className="zn:w-5 zn:h-4"
				/>
				<span className="zn:font-medium">{getSelectedLanguage().name}</span>
			</div>

			{/* Content Container - Similar to PageStructureView */}
			<div className="zn:!bg-gray-50 zn:rounded-[20px] zn:!p-4">
				{/* Search Input */}
				<div className="zn:!mb-4">
					<div className="zn:relative">
						<Search className="zn:absolute zn:left-3 zn:top-1/2 zn:transform zn:-translate-y-1/2 zn:size-4 zn:text-default-400" />
						<Input
							ref={searchInputRef}
							type="text"
							placeholder={__("Search languages...", "accessibly-wp")}
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className="zn:w-full zn:!pl-10 zn:!pr-4 zn:!py-3 zn:text-sm zn:!border zn:!border-default-200 zn:rounded-lg zn:!bg-white zn:outline-none zn:focus:!border-primary focus:zn:!ring-0"
						/>
					</div>
				</div>

				{/* Languages List */}
				<div className="zn:!mt-4">
					<ScrollArea className="zn:h-[400px]">
						{filteredLanguages.length > 0 ? (
							<div className="zn:space-y-2">
								{filteredLanguages.map((lang) => (
									<div
										key={lang.code}
										onClick={() => handleSelect(lang.code)}
										className={cn(
											"zn:!py-3 zn:!px-4 zn:group zn:!flex zn:!gap-3 zn:!bg-white zn:rounded-md zn:hover:!bg-gray-100 zn:transition zn:cursor-pointer zn:!text-sm zn:!items-center",
											{
												"zn:!bg-primary-icon zn:!text-white zn:hover:!bg-primary-icon/90":
													currentLanguage === lang.code,
											},
										)}
									>
										<FlagIcon
											countryCode={lang.code}
											className="zn:w-5 zn:h-4 zn:flex-none"
										/>
										<span
											className={cn(
												"zn:flex-1 zn:font-medium zn:text-default-800",
												{
													"zn:!text-white": currentLanguage === lang.code,
												},
											)}
										>
											{lang.name}
										</span>
										{currentLanguage === lang.code && (
											<CircleCheck className="zn:size-5 zn:flex-none" />
										)}
									</div>
								))}
							</div>
						) : (
							<div className="zn:text-center zn:!py-8 zn:!text-gray-500">
								{__("No languages found", "accessibly-wp")}
							</div>
						)}
					</ScrollArea>
				</div>
			</div>
		</div>
	);
};

export default LanguageSwitcher;
